import type { CurrenciesOfferedSettings } from '@norvix/utils';
import { useQuery } from '@tanstack/react-query';

import { apiFetch } from './client.js';
import type { SettingsHistoryEntry } from './settings-history.api.js';

/**
 * Settings → Organisation → Currencies offered (DEV-162). Every staff role
 * reads the list (it fills the currency pickers); only Admin changes it.
 */

export interface StoredCurrencies {
  value: CurrenciesOfferedSettings;
  version: number;
  updatedAt: string;
  updatedBy: string | null;
}

export interface CurrenciesRestorePreview {
  version: number;
  expectedVersion: number;
  changes: SettingsHistoryEntry['changes'];
}

export const currenciesApi = {
  get: (): Promise<StoredCurrencies> => apiFetch('/admin/settings/currencies'),
  save: (input: { expectedVersion: number; value: CurrenciesOfferedSettings; reason: string }): Promise<StoredCurrencies & { changed: boolean }> =>
    apiFetch('/admin/settings/currencies', { method: 'PUT', body: input }),
  preview: (version: number, expectedVersion: number): Promise<CurrenciesRestorePreview> =>
    apiFetch('/admin/settings/currencies/restore', { method: 'POST', body: { version, expectedVersion, dryRun: true } }),
  restore: (input: { version: number; expectedVersion: number; reason: string }): Promise<StoredCurrencies & { changed: boolean; restoredFromVersion: number }> =>
    apiFetch('/admin/settings/currencies/restore', { method: 'POST', body: input }),
};

export const CURRENCIES_KEY = ['currencies-offered'] as const;

/**
 * The offered codes, in order, for a currency picker. Empty while loading or
 * when the list cannot be read: a picker then shows only the codes already in
 * use, never a list typed into this bundle.
 */
export function useOfferedCurrencies(): { readonly currencies: readonly string[]; readonly isLoading: boolean; readonly isError: boolean } {
  const query = useQuery({ queryKey: CURRENCIES_KEY, queryFn: () => currenciesApi.get(), staleTime: 5 * 60 * 1000 });
  // Checked rather than trusted: an answer of another shape reads as nothing offered, never as a crash.
  const listed: unknown = (query.data as { value?: { currencies?: unknown } } | undefined)?.value?.currencies;
  const currencies = Array.isArray(listed) ? listed.filter((c): c is string => typeof c === 'string') : [];
  return { currencies, isLoading: query.isLoading, isError: query.isError };
}
